import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useClient } from './ClientContext';
import { getPlanFeatures, hasPlanFeature, type CompanyPlanData, type FeatureKey } from '../config/planFeatures';
import { apiClient } from '../api/apiClient';
import { onInvalidate } from '../lib/live';
import { useQueryClient } from '@tanstack/react-query';
import { companyKeys, homeKeys, authKeys } from '../lib/queryKeys';
import { hasFullPermissions } from '../config/permissionsConfig';

type FeaturesValue = {
  company: string;
  enabledFeatures: string[] | null;
  hiddenFromSidebar: string[];
  lockedFeatures: string[];
  plan: string | null;
  planFeatures: ReturnType<typeof getPlanFeatures> | null;
  loading: boolean;
  isReady: boolean; // True when features have been loaded at least once (or hydrated from localStorage)
  error?: any;
  refresh: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
};

const defaultValue: FeaturesValue = {
  company: '',
  enabledFeatures: null,
  hiddenFromSidebar: [],
  lockedFeatures: [],
  plan: null,
  planFeatures: null,
  loading: false,
  isReady: false,
  error: undefined,
  refresh: async () => {},
  hasFeature: () => false,
};

const FeaturesContext = createContext<FeaturesValue>(defaultValue);

interface CacheEntry {
  enabledFeatures: string[] | null;
  hiddenFromSidebar: string[];
  lockedFeatures: string[];
  plan: string | null;
}

export const FeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { client } = useClient();
  const location = useLocation();
  const queryClient = useQueryClient();
  // CRITICAL FIX: Use apiClient baseURL instead of window.location.origin
  // This ensures we use backend.crmtest.cloud consistently, matching where cookies are set
  const baseUrl = useMemo(() => {
    const apiClientBase = apiClient?.defaults?.baseURL;
    if (apiClientBase) {
      return apiClientBase.replace(/\/+$/, '');
    }
    // Fallback for edge cases
    return (process.env.REACT_APP_API_URL || 
            (typeof window !== 'undefined' && window.location.hostname.includes('crmtest.cloud') 
              ? 'https://backend.crmtest.cloud' 
              : window.location.origin)).replace(/\/+$/, '');
  }, []);

  const company = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length > 0 && segments[0] !== 'superadmin') {
      return segments[0];
    }
    const stored = typeof window !== 'undefined' ? localStorage.getItem('currentCompany') || '' : '';
    if (stored) {
      return stored;
    }
    if (client?.company) {
      return client.company;
    }
    return '';
  }, [location.pathname, client?.company]);

  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const pendingRef = useRef<Map<string, Promise<CacheEntry>>>(new Map());
  const reloadingRef = useRef<Set<string>>(new Set()); // Track companies currently reloading to prevent loops
  const lastReloadTimeRef = useRef<Map<string, number>>(new Map()); // Track last reload time for debouncing

  // Helper to read from localStorage
  const getCachedFeatures = useCallback((slug: string): { hiddenFromSidebar: string[]; updatedAt: string | null } | null => {
    if (typeof window === 'undefined' || !slug || slug === 'superadmin') return null;
    
    try {
      const cached = localStorage.getItem(`companyFeatures:${slug}`);
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      return {
        hiddenFromSidebar: Array.isArray(parsed.hiddenFromSidebar) ? parsed.hiddenFromSidebar : [],
        updatedAt: parsed.updatedAt || null,
      };
    } catch (e) {
      console.warn('[FeaturesProvider] Failed to parse cached features:', e);
      return null;
    }
  }, []);

  // Helper to write to localStorage
  const setCachedFeatures = useCallback((slug: string, hiddenFromSidebar: string[], updatedAt: string) => {
    if (typeof window === 'undefined' || !slug || slug === 'superadmin') return;
    
    try {
      localStorage.setItem(`companyFeatures:${slug}`, JSON.stringify({
        hiddenFromSidebar,
        updatedAt,
      }));
    } catch (e) {
      console.warn('[FeaturesProvider] Failed to cache features:', e);
    }
  }, []);

  // Initialize state with localStorage hydration
  const initialState = useMemo(() => {
    const cached = company ? getCachedFeatures(company) : null;
    return {
      enabledFeatures: null,
      hiddenFromSidebar: cached?.hiddenFromSidebar || [],
      lockedFeatures: [],
      plan: null,
      planFeatures: null,
      loading: false,
      isReady: !!cached, // Ready if we have cached data, otherwise wait for API
      error: undefined,
    };
  }, [company, getCachedFeatures]);

  const [state, setState] = useState<Omit<FeaturesValue, 'company' | 'refresh' | 'hasFeature'>>(initialState);

  const load = useCallback(async (slug: string, force = false): Promise<CacheEntry> => {
    if (!slug || slug === 'superadmin' || client?.role === 'superadmin') {
      const fallback: CacheEntry = { enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null };
      cacheRef.current.set(slug, fallback);
      setState({ enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null, planFeatures: null, loading: false, isReady: true, error: undefined });
      return fallback;
    }

    // Note: localStorage hydration is handled in useEffect when company changes
    // This ensures it happens synchronously before any render

    // DISABLED CACHE: Always fetch fresh data to ensure badge/plan changes appear immediately
    // if (!force && cacheRef.current.has(slug)) {
    //   const cached = cacheRef.current.get(slug)!;
    //   const planData: CompanyPlanData = { plan: cached.plan };
    //   const planFeatures = getPlanFeatures(planData);
    //   setState({ enabledFeatures: cached.enabledFeatures, hiddenFromSidebar: cached.hiddenFromSidebar, lockedFeatures: cached.lockedFeatures, plan: cached.plan, planFeatures, loading: false, error: undefined });
    //   return cached;
    // }

    if (pendingRef.current.has(slug)) {
      return pendingRef.current.get(slug)!;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      const fallback: CacheEntry = { enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null };
      cacheRef.current.set(slug, fallback);
      setState({ enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null, planFeatures: null, loading: false, isReady: true, error: undefined });
      return fallback;
    }

    setState(prev => ({ ...prev, loading: true, error: undefined }));

    // CRITICAL FIX: Use apiClient instead of axios directly to ensure consistent base URL and cookie handling
    const request = apiClient
      .get(`/api/${slug}/admin/features`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .then((res) => {
        // Handle both direct response and wrapped response structure
        const responseData = res.data?.data || res.data;
        const enabled = responseData?.enabledFeatures;
        const hidden = responseData?.hiddenFromSidebar || [];
        const locked = responseData?.lockedFeatures || [];
        const plan = responseData?.plan || null;
        const featuresUpdatedAt = responseData?.featuresUpdatedAt ? new Date(responseData.featuresUpdatedAt) : new Date();

        // Calculate plan features based on plan
        const planData: CompanyPlanData = { plan };
        const planFeatures = getPlanFeatures(planData);
        
        // CRITICAL: Create new array references to ensure React detects changes
        const newEnabledFeatures = Array.isArray(enabled) && enabled.length > 0 ? [...enabled] : null;
        const newHiddenFromSidebar = Array.isArray(hidden) ? [...hidden] : [];
        const newLockedFeatures = Array.isArray(locked) ? [...locked] : [];
        
        const entry: CacheEntry = {
          enabledFeatures: newEnabledFeatures,
          hiddenFromSidebar: newHiddenFromSidebar,
          lockedFeatures: newLockedFeatures,
          plan: plan,
        };
        cacheRef.current.set(slug, entry);
        
        // CRITICAL: Update localStorage with fresh data
        setCachedFeatures(slug, newHiddenFromSidebar, featuresUpdatedAt.toISOString());
        
        setState({ 
          enabledFeatures: newEnabledFeatures, 
          hiddenFromSidebar: newHiddenFromSidebar, 
          lockedFeatures: newLockedFeatures, 
          plan: entry.plan,
          planFeatures,
          loading: false,
          isReady: true, // Mark as ready after API response
          error: undefined 
        });
        
        return entry;
      })
      .catch((err) => {
        if (err?.response?.status === 404 || err?.response?.status === 403 || err?.response?.status === 401) {
          const entry: CacheEntry = { enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null };
          cacheRef.current.set(slug, entry);
          setState({ enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null, planFeatures: null, loading: false, isReady: true, error: undefined });
          return entry;
        }
        console.error('[FeaturesProvider] Failed to fetch company features:', err);
        const entry: CacheEntry = { enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null };
        // On error, keep cached hiddenFromSidebar if available
        const cached = getCachedFeatures(slug);
        setState({ 
          enabledFeatures: entry.enabledFeatures, 
          hiddenFromSidebar: cached?.hiddenFromSidebar || entry.hiddenFromSidebar, 
          lockedFeatures: entry.lockedFeatures, 
          plan: entry.plan, 
          planFeatures: null, 
          loading: false, 
          isReady: true, // Mark as ready even on error (use cached data)
          error: err 
        });
        return entry;
      })
      .finally(() => {
        pendingRef.current.delete(slug);
      });

  pendingRef.current.set(slug, request);
  return request;
  }, [baseUrl, client?.role, state.isReady, getCachedFeatures, setCachedFeatures]);

  useEffect(() => {
    if (!company) {
      setState({ enabledFeatures: null, hiddenFromSidebar: [], lockedFeatures: [], plan: null, planFeatures: null, loading: false, isReady: false, error: undefined });
      return;
    }
    
    // CRITICAL: Hydrate from localStorage immediately when company changes
    const cached = getCachedFeatures(company);
    if (cached) {
      setState(prev => ({
        ...prev,
        hiddenFromSidebar: cached.hiddenFromSidebar,
        isReady: true, // Mark as ready immediately
        loading: true, // Still loading from API
      }));
    }
    
    let cancelled = false;
    load(company).then((entry) => {
      if (cancelled) return;
      // Calculate plan features
      const planData: CompanyPlanData = { plan: entry.plan };
      const planFeatures = getPlanFeatures(planData);
      setState({ 
        enabledFeatures: entry.enabledFeatures, 
        hiddenFromSidebar: entry.hiddenFromSidebar, 
        lockedFeatures: entry.lockedFeatures, 
        plan: entry.plan,
        planFeatures,
        loading: false,
        isReady: true,
        error: undefined 
      });
    });
    return () => {
      cancelled = true;
    };
  }, [company, load, getCachedFeatures]);

  // Listen for plan changes and company updates to refresh plan features
  useEffect(() => {
    const handlePlanChange = () => {
      if (company && company !== 'superadmin') {
        // Debounce: Don't reload if we just reloaded recently (within 1 second)
        const lastReload = lastReloadTimeRef.current.get(company) || 0;
        const now = Date.now();
        if (now - lastReload < 1000) {
          return;
        }
        
        // Prevent duplicate reloads
        if (reloadingRef.current.has(company)) {
          return;
        }
        
        reloadingRef.current.add(company);
        lastReloadTimeRef.current.set(company, now);
        cacheRef.current.delete(company);
        pendingRef.current.delete(company);
        load(company, true).finally(() => {
          reloadingRef.current.delete(company);
        });
      }
    };

    const handleCompanyUpdate = () => {
      if (company && company !== 'superadmin') {
        // Debounce: Don't reload if we just reloaded recently (within 1 second)
        const lastReload = lastReloadTimeRef.current.get(company) || 0;
        const now = Date.now();
        if (now - lastReload < 1000) {
          return;
        }
        
        // Prevent duplicate reloads
        if (reloadingRef.current.has(company)) {
          return;
        }
        
        reloadingRef.current.add(company);
        lastReloadTimeRef.current.set(company, now);
        cacheRef.current.delete(company);
        pendingRef.current.delete(company);
        load(company, true).finally(() => {
          reloadingRef.current.delete(company);
        });
      }
    };

    window.addEventListener('company-plan-changed', handlePlanChange);
    window.addEventListener('company-updated', handleCompanyUpdate);

    return () => {
      window.removeEventListener('company-plan-changed', handlePlanChange);
      window.removeEventListener('company-updated', handleCompanyUpdate);
    };
  }, [company, load]);

  // Listen for company-features-updated event to refresh features
  useEffect(() => {
    const handleFeaturesUpdate = (event?: CustomEvent) => {
      if (company && company !== 'superadmin') {
        // Debounce: Don't reload if we just reloaded recently (within 1 second)
        const lastReload = lastReloadTimeRef.current.get(company) || 0;
        const now = Date.now();
        if (now - lastReload < 1000) {
          return;
        }
        
        // Prevent duplicate reloads
        if (reloadingRef.current.has(company)) {
          return;
        }
        
        reloadingRef.current.add(company);
        lastReloadTimeRef.current.set(company, now);
        
        // Clear all caches to force fresh data - prevent reverting to old badge
        cacheRef.current.delete(company);
        pendingRef.current.delete(company);
        
        // Clear localStorage cache for features and badge
        try {
          localStorage.removeItem(`company_features_${company}`);
          localStorage.removeItem(`company_features_ts_${company}`);
          localStorage.removeItem(`company_badge_${company}`);
          localStorage.removeItem(`companyFeatures:${company}`); // Clear our new cache key
        } catch (e) {
          console.error('[FeaturesProvider] Failed to clear cache:', e);
        }
        
        // CRITICAL: Invalidate ALL React Query caches that depend on company plan/features
        queryClient.invalidateQueries({ queryKey: companyKeys.features(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: companyKeys.plan(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: companyKeys.settings(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: authKeys.me(company, 'tenant') }); // Client data includes plan info
        queryClient.invalidateQueries({ queryKey: homeKeys.badge(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: homeKeys.plan(company, 'tenant') });
        
        // CRITICAL: Invalidate header bootstrap query (uses long staleTime, must be explicitly invalidated)
        queryClient.invalidateQueries({ 
          queryKey: ['header', 'bootstrap', company],
          refetchType: 'active', // Force immediate refetch for active queries
        });
        
        // Also invalidate by pattern to catch all variations
        queryClient.invalidateQueries({ 
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && 
                   (key[0] === 'header' || key[0] === 'bootstrap') &&
                   (key.includes(company) || key.includes('tenant'));
          },
          refetchType: 'active',
        });
        
        
        // Reload features immediately with fresh data
        load(company, true).then((entry) => {
          // Recalculate plan features
          const planData: CompanyPlanData = { plan: entry.plan };
          const planFeatures = getPlanFeatures(planData);
          
          // CRITICAL: Create new array references to ensure React detects changes
          const newEnabledFeatures = entry.enabledFeatures ? [...entry.enabledFeatures] : null;
          const newHiddenFromSidebar = entry.hiddenFromSidebar ? [...entry.hiddenFromSidebar] : [];
          const newLockedFeatures = entry.lockedFeatures ? [...entry.lockedFeatures] : [];
          
          // Update localStorage with fresh data
          setCachedFeatures(company, newHiddenFromSidebar, new Date().toISOString());
          
          setState({
            enabledFeatures: newEnabledFeatures,
            hiddenFromSidebar: newHiddenFromSidebar,
            lockedFeatures: newLockedFeatures,
            plan: entry.plan,
            planFeatures,
            loading: false,
            isReady: true,
            error: undefined
          });
          
          // DON'T dispatch another event here - it would cause a loop
          // The websocket event already triggered this, no need to dispatch again
        }).finally(() => {
          reloadingRef.current.delete(company);
        });
      }
    };

    window.addEventListener('company-features-updated', handleFeaturesUpdate as EventListener);
    return () => {
      window.removeEventListener('company-features-updated', handleFeaturesUpdate as EventListener);
    };
  }, [company, load, queryClient]);

  // Listen for websocket invalidate events for real-time updates
  useEffect(() => {
    if (!company || company === 'superadmin') return;

    const unsubscribe = onInvalidate((payload: any) => {
      const payloadType = payload?.type;
      const payloadCompany = payload?.company || payload?.companySlug;
      
      // Only handle events for this company (case-insensitive match)
      if (payloadCompany && payloadCompany.toLowerCase() !== company.toLowerCase()) {
        return;
      }
      
      // Handle company-features and company-plan updates
      if (payloadType === 'company-features' || payloadType === 'company-plan') {
        // Debounce: Don't reload if we just reloaded recently (within 1 second)
        const lastReload = lastReloadTimeRef.current.get(company) || 0;
        const now = Date.now();
        if (now - lastReload < 1000) {
          return;
        }
        
        // Prevent duplicate reloads
        if (reloadingRef.current.has(company)) {
          return;
        }
        
        reloadingRef.current.add(company);
        lastReloadTimeRef.current.set(company, now);
        
        // Clear all caches to force fresh data
        cacheRef.current.delete(company);
        pendingRef.current.delete(company);
        
        // Clear localStorage cache for features and badge
        try {
          localStorage.removeItem(`company_features_${company}`);
          localStorage.removeItem(`company_features_ts_${company}`);
          localStorage.removeItem(`company_badge_${company}`);
        } catch (e) {
          console.error('[FeaturesProvider] Failed to clear cache:', e);
        }
        
        // CRITICAL: Invalidate ALL React Query caches that depend on company plan/features
        // This ensures header, limits, and all plan-dependent data updates in real-time
        queryClient.invalidateQueries({ queryKey: companyKeys.features(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: companyKeys.plan(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: companyKeys.settings(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: authKeys.me(company, 'tenant') }); // Client data includes plan info
        queryClient.invalidateQueries({ queryKey: homeKeys.badge(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: homeKeys.plan(company, 'tenant') });
        queryClient.invalidateQueries({ queryKey: homeKeys.catalog(company, 'tenant') });
        
        // CRITICAL: Invalidate header bootstrap query (uses long staleTime, must be explicitly invalidated)
        queryClient.invalidateQueries({ 
          queryKey: ['header', 'bootstrap', company],
          refetchType: 'active', // Force immediate refetch for active queries
        });
        
        // Also invalidate by pattern to catch all variations
        queryClient.invalidateQueries({ 
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && 
                   (key[0] === 'header' || key[0] === 'bootstrap') &&
                   (key.includes(company) || key.includes('tenant'));
          },
          refetchType: 'active',
        });
        
        
        // Reload features immediately with fresh data
        load(company, true).then((entry) => {
          // Recalculate plan features
          const planData: CompanyPlanData = { plan: entry.plan };
          const planFeatures = getPlanFeatures(planData);
          
          // CRITICAL: Create new array references to ensure React detects changes
          const newEnabledFeatures = entry.enabledFeatures ? [...entry.enabledFeatures] : null;
          const newHiddenFromSidebar = entry.hiddenFromSidebar ? [...entry.hiddenFromSidebar] : [];
          const newLockedFeatures = entry.lockedFeatures ? [...entry.lockedFeatures] : [];
          
          // Update localStorage with fresh data (use current timestamp as updatedAt)
          setCachedFeatures(company, newHiddenFromSidebar, new Date().toISOString());
          
          setState({
            enabledFeatures: newEnabledFeatures,
            hiddenFromSidebar: newHiddenFromSidebar,
            lockedFeatures: newLockedFeatures,
            plan: entry.plan,
            planFeatures,
            loading: false,
            isReady: true,
            error: undefined
          });
          
          // Dispatch custom event to notify components (Header, Sidebar, etc.)
          // This is safe because we've debounced and prevented duplicate reloads
          window.dispatchEvent(new CustomEvent('company-features-updated', {
            detail: {
              company,
              enabledFeatures: newEnabledFeatures,
              plan: entry.plan
            }
          }));
        }).finally(() => {
          reloadingRef.current.delete(company);
        });
      }
    });

    return unsubscribe;
  }, [company, load, queryClient, setCachedFeatures]);

  const refresh = useCallback(async () => {
    if (!company) return;
    cacheRef.current.delete(company);
    await load(company, true);
  }, [company, load]);

  // Helper function to check if a feature is available
  // Combines plan features with enabled features from SuperAdmin
  const hasFeature = useCallback((feature: string): boolean => {
    // SuperAdmin always has access
    if (client?.role === 'superadmin' || company === 'superadmin') {
      return true;
    }

    // Check if manager has full permissions (admin role) - give them full access
    const userPermissions = Array.isArray(client?.permissions) ? (client?.permissions || []) : [];
    const isManagerWithAdminRole = client?.role === 'manager' && hasFullPermissions(userPermissions);
    
    // Managers with full permissions (admin role) get full access like admins
    if (isManagerWithAdminRole) {
      return true;
    }

    // Map feature aliases (managers -> users)
    const featureMap: Record<string, string> = {
      'managers': 'users',
      'users': 'users',
    };
    const mappedFeature = featureMap[feature] || feature;

    // Check plan features first
    if (state.planFeatures) {
      const planFeatureKey = mappedFeature as FeatureKey;
      // Check both the mapped feature and original feature
      if (state.planFeatures[planFeatureKey as keyof typeof state.planFeatures] === true ||
          state.planFeatures[feature as keyof typeof state.planFeatures] === true) {
        return true;
      }
    }

    // Check enabled features from SuperAdmin (allows override/enable additional features)
    if (state.enabledFeatures !== null) {
      if (state.enabledFeatures.includes(feature) || state.enabledFeatures.includes(mappedFeature)) {
        return true;
      }
    }

    // If enabledFeatures is null, it means we haven't loaded yet or it's a new company
    // In this case, fall back to plan features
    if (state.enabledFeatures === null && state.planFeatures) {
      const planFeatureKey = mappedFeature as FeatureKey;
      return state.planFeatures[planFeatureKey as keyof typeof state.planFeatures] === true ||
             state.planFeatures[feature as keyof typeof state.planFeatures] === true;
    }

    return false;
  }, [client?.role, client?.permissions, company, state.planFeatures, state.enabledFeatures]);

  const value = useMemo<FeaturesValue>(() => ({
    company,
    enabledFeatures: state.enabledFeatures,
    hiddenFromSidebar: state.hiddenFromSidebar,
    lockedFeatures: state.lockedFeatures,
    plan: state.plan,
    planFeatures: state.planFeatures,
    loading: state.loading,
    isReady: state.isReady,
    error: state.error,
    refresh,
    hasFeature,
  }), [company, state.enabledFeatures, state.hiddenFromSidebar, state.lockedFeatures, state.plan, state.planFeatures, state.loading, state.isReady, state.error, refresh, hasFeature]);

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};

export function useCompanyFeatures() {
  return useContext(FeaturesContext);
}
